import { makeAuthedFetch } from "@nu-assist/shared-fe";
import { type ReactNode, createContext, useContext, useMemo } from "react";

/** Minimal fetch-shaped type — matches the return of `makeAuthedFetch`. */
export type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

const AdvisoryClientContext = createContext<FetchLike | null>(null);

export interface AdvisoryClientProviderProps {
  children: ReactNode;
  /** nu-assist API base (e.g. `import.meta.env.VITE_API_URL`). The refresh
   *  call (`POST /auth/refresh`) goes here — NOT to the advisory API. */
  apiBase: string;
  /** Called once when the refresh cookie is gone/expired. Responsibility:
   *  clear the query cache + navigate to /login. */
  onSessionExpired: () => void;
}

/**
 * Provides a `makeAuthedFetch`-backed fetch to all advisory API callers in
 * the protected route tree. Mounted once in `AuthGuard` so the
 * `onSessionExpired` callback (clear cache + /login) is wired in one place.
 *
 * On a 401 from the advisory API, the underlying `makeAuthedFetch` single-
 * flights a `POST /auth/refresh` to `apiBase`, sets a fresh session cookie,
 * and retries the original request once. If the refresh also fails (hard
 * expiry), `onSessionExpired` fires and the call rejects — users land on
 * /login automatically.
 */
export function AdvisoryClientProvider({
  children,
  apiBase,
  onSessionExpired,
}: AdvisoryClientProviderProps) {
  const advisoryFetch = useMemo(
    () => makeAuthedFetch({ apiBase, onSessionExpired }),
    [apiBase, onSessionExpired]
  );

  return (
    <AdvisoryClientContext.Provider value={advisoryFetch}>
      {children}
    </AdvisoryClientContext.Provider>
  );
}

/**
 * Returns the session-refresh-aware fetch for advisory API calls.
 * Must be called inside `AdvisoryClientProvider` (mounted by `AuthGuard`).
 */
export function useAdvisoryFetch(): FetchLike {
  const ctx = useContext(AdvisoryClientContext);
  if (!ctx) {
    throw new Error("useAdvisoryFetch must be called inside AdvisoryClientProvider");
  }
  return ctx;
}
